/**
 * UI translation runtime — wraps the dashboard in a TranslationProvider
 * so any component can call `useT()` to translate English literals into
 * the user's selected UI language on the fly.
 *
 * How it works:
 *   1. `UiLanguageProvider` reads `/api/i18n/preference` on mount and
 *      maintains the current ui_language in React state + localStorage.
 *   2. Components call `const t = useT();` then `t("Dashboard")` —
 *      returns the English string instantly when:
 *        • lang is "en", OR
 *        • the translation is already in cache.
 *   3. For misses, the hook adds the string to a debounced queue.
 *      Every 200 ms the queue is flushed to
 *      `POST /api/i18n/translate-ui` as one batched request.
 *      Once responses come back, the cache + state update and
 *      mounted components re-render with the translated string.
 *
 * localStorage cache key: `bf_ui_i18n_cache_v1` — flat map of
 *   `${lang}::${english}` → `translated`. Survives page reloads so
 *   the dashboard renders in the user's language instantly on cold-start.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const CACHE_KEY = "bf_ui_i18n_cache_v1";
const LANG_KEY  = "bf_ui_lang";
const FLUSH_MS  = 200;

const UiLanguageCtx = createContext({
  lang: "en",
  setLang: () => {},
  t: (s) => s,
  ready: false,
});

function loadCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function saveCache(cache) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch { /* quota exceeded — drop silently */ }
}

export function UiLanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try { return localStorage.getItem(LANG_KEY) || "en"; } catch { return "en"; }
  });
  const cacheRef = useRef(loadCache());
  const [version, setVersion] = useState(0);   // bumps to trigger re-renders
  const pendingRef = useRef(new Set());        // strings queued for translation
  const flushTimerRef = useRef(null);
  const inflightRef = useRef(false);
  const [ready, setReady] = useState(false);

  // Pull saved preference from the backend on mount (cookie session)
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/i18n/preference`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => {
        if (cancelled || !d) return;
        const next = d.ui_language || "en";
        if (next !== lang) {
          setLangState(next);
          try { localStorage.setItem(LANG_KEY, next); } catch { /* ignore */ }
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setReady(true); });
    return () => { cancelled = true; };
  }, []);

  // Public setter — also persists to localStorage + backend
  const setLang = useCallback((code) => {
    setLangState(code);
    try { localStorage.setItem(LANG_KEY, code); } catch { /* ignore */ }
    fetch(`${API_BASE}/api/i18n/preference`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code, scope: "ui" }),
    }).catch(() => { /* not signed in → silently keep local */ });
    setVersion((v) => v + 1);
  }, []);

  // Flush the pending queue
  const flush = useCallback(async () => {
    if (inflightRef.current) return;
    if (lang === "en") { pendingRef.current.clear(); return; }
    const batch = Array.from(pendingRef.current);
    pendingRef.current.clear();
    if (batch.length === 0) return;
    inflightRef.current = true;
    try {
      const r = await fetch(`${API_BASE}/api/i18n/translate-ui`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ strings: batch, target_lang: lang }),
      });
      if (!r.ok) throw new Error(`status ${r.status}`);
      const data = await r.json();
      const next = { ...cacheRef.current };
      for (const en of batch) {
        const tr = (data.translations || {})[en] || en;
        next[`${lang}::${en}`] = tr;
      }
      cacheRef.current = next;
      saveCache(next);
      setVersion((v) => v + 1);
    } catch {
      // Mark batch as "tried" so we don't loop on it forever — fall back to English
      const next = { ...cacheRef.current };
      for (const en of batch) {
        if (!next[`${lang}::${en}`]) next[`${lang}::${en}`] = en;
      }
      cacheRef.current = next;
    } finally {
      inflightRef.current = false;
      // If new strings queued while we were inflight, flush again
      if (pendingRef.current.size > 0) {
        flushTimerRef.current = setTimeout(flush, FLUSH_MS);
      }
    }
  }, [lang]);

  // Queue scheduler
  const enqueue = useCallback((s) => {
    if (!s || lang === "en") return;
    if (cacheRef.current[`${lang}::${s}`]) return;
    pendingRef.current.add(s);
    if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    flushTimerRef.current = setTimeout(flush, FLUSH_MS);
  }, [lang, flush]);

  // The translation helper exposed to consumers
  const t = useCallback((s) => {
    if (!s || typeof s !== "string") return s;
    if (lang === "en") return s;
    const cached = cacheRef.current[`${lang}::${s}`];
    if (cached) return cached;
    enqueue(s);
    return s; // graceful fallback while LLM is working
  }, [lang, enqueue]);

  const ctx = useMemo(() => ({ lang, setLang, t, ready, version }),
                      [lang, setLang, t, ready, version]);

  return <UiLanguageCtx.Provider value={ctx}>{children}</UiLanguageCtx.Provider>;
}

/** Returns the current ui language (e.g. "en", "es"). */
export function useUiLanguage() {
  return useContext(UiLanguageCtx);
}

/** Returns a `t(english)` function. Components must call this hook
 * inside their render to subscribe to language changes. */
export function useT() {
  return useContext(UiLanguageCtx).t;
}

/** Imperative wrapper component — useful for inline JSX without a hook
 * call: `<T>Some english</T>`. */
export function T({ children }) {
  const t = useT();
  return <>{typeof children === "string" ? t(children) : children}</>;
}
